import * as React from 'react';

import {useQuery} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {AudienceSortType} from 'nutshell-graphql-types';
import type {
    GetAudiences as GetAudiencesQuery,
    GetAudiencesVariables as GetAudiencesQueryVariables,
    AudienceStub as Audience,
} from 'nutshell-graphql-types';

import {getAudienceListQueryVariables} from '../helpers';

import GET_AUDIENCES from './graphql/queries/get-audiences.graphql';

type Props = {
    isArchived: boolean | null | undefined;
    sortOrder: AudienceSortType;
    searchQuery?: string;
    creatorUserId?: string;
    lastUpdatedTime?: string | null | undefined;
    onlyPublic?: boolean;
    children: (arg1: {
        audiences: Audience[];
        isLoading: boolean;
        error: string | null | undefined;
        isFiltering: boolean;
        fetchMore: (search: string) => Promise<any>;
        fetchMoreRows: () => Promise<any>;
        hasNextPage: boolean;
    }) => React.ReactElement;
};

const LIMIT = 20;

export function AudienceListQuery(props: Props) {
    const variables = getAudienceListQueryVariables({
        isArchived: Boolean(props.isArchived),
        sort: props.sortOrder,
        search: props.searchQuery,
        creatorUserId: props.creatorUserId,
        lastUpdatedTime: props.lastUpdatedTime,
        onlyPublic: Boolean(props.onlyPublic),
    });

    const {
        data,
        error,
        loading: isLoading,
        fetchMore,
    } = useQuery<GetAudiencesQuery, GetAudiencesQueryVariables>(GET_AUDIENCES, {
        variables: variables,
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const audiencesData = data ? data.audiences : null;

    const audiences = audiencesData
        ? audiencesData.edges.map((audience) => {
              return audience.node;
          })
        : [];

    const isFilteringOnCreatorUserId = Boolean(props.creatorUserId);
    const isFilteringOnLastUpdatedTime = Boolean(props.lastUpdatedTime);
    const isSearching = Boolean(props.searchQuery && props.searchQuery.length);

    // This is what is used to get the next page of audiences for the paginated list view
    const fetchMoreRows = () => {
        return fetchMore({
            variables: {
                first: LIMIT,
                after: audiencesData ? audiencesData.pageInfo.endCursor : null,
            },
            // @ts-expect-error
            updateQuery: (
                prev: GetAudiencesQuery,
                newResults: {
                    fetchMoreResult: GetAudiencesQuery;
                }
            ) => {
                const oldAudiences = prev.audiences;
                const fetchedAudiences = newResults.fetchMoreResult.audiences;

                const newAudiences = {
                    ...oldAudiences,
                    pageInfo: fetchedAudiences.pageInfo,
                    edges: oldAudiences.edges.concat(fetchedAudiences.edges),
                } as const;

                return {audiences: newAudiences};
            },
        });
    };

    const hasNextPage = audiencesData ? audiencesData.pageInfo.hasNextPage : false;

    return props.children({
        audiences,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
        isLoading,
        isFiltering: isFilteringOnCreatorUserId || isFilteringOnLastUpdatedTime || isSearching,
        fetchMore: (search: string) => {
            return fetchMore({
                query: GET_AUDIENCES,
                variables: getAudienceListQueryVariables({
                    isArchived: Boolean(props.isArchived),
                    sort: props.sortOrder,
                    search,
                    creatorUserId: props.creatorUserId,
                    lastUpdatedTime: props.lastUpdatedTime,
                    onlyPublic: Boolean(props.onlyPublic),
                }),
                // @ts-expect-error
                updateQuery: (previousResult, {fetchMoreResult}) => {
                    if (!fetchMoreResult || !previousResult) return;

                    return {
                        tags: {
                            // @ts-expect-error
                            __typename: 'AudiencesConnection',
                            ...fetchMoreResult.audiences,
                        },
                    };
                },
            });
        },
        fetchMoreRows,
        hasNextPage,
    });
}
